import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  type PublicSurvey,
  respondToPublicSurvey,
  viewPublicSurvey,
} from '@/api/surveys';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/**
 * Public survey page (Epic 226 "utilizable"). Mounted outside the AppShell —
 * no sidebar, no auth. Resolves the survey by its share token, renders the
 * questions, and submits a response (requires an email unless the survey
 * allows anonymous responses).
 */
export function PublicSurveyPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [survey, setSurvey] = useState<PublicSurvey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    viewPublicSurvey(token)
      .then(setSurvey)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!token || !survey) return;
    setSubmitting(true);
    try {
      await respondToPublicSurvey(
        token,
        answers,
        survey.allowAnonymous ? undefined : email.trim(),
      );
      setDone(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <CenteredCard>{t('common.loading')}</CenteredCard>;
  }
  if (error || !survey) {
    return <CenteredCard>{t('surveys.public.notFound')}</CenteredCard>;
  }
  if (done) {
    return <CenteredCard>{t('surveys.public.thankYou')}</CenteredCard>;
  }

  return (
    <div className="mx-auto min-h-screen max-w-xl px-4 py-12">
      <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{survey.title}</h1>
      {survey.description && (
        <p className="mt-2 text-sm text-[var(--color-lop-text-secondary)]">{survey.description}</p>
      )}
      <form onSubmit={submit} className="mt-6 space-y-5">
        {survey.questions.map((q) => (
          <label key={q.id} className="block space-y-1">
            <span className="text-sm font-medium text-[var(--color-lop-text-primary)]">{q.label}</span>
            <Input
              value={answers[q.id] ?? ''}
              onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
            />
          </label>
        ))}
        {!survey.allowAnonymous && (
          <label className="block space-y-1">
            <span className="text-sm font-medium text-[var(--color-lop-text-primary)]">
              {t('surveys.public.email')}
            </span>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        )}
        <Button type="submit" disabled={submitting} data-testid="public-survey-submit">
          {submitting ? t('common.saving') : t('surveys.public.submit')}
        </Button>
      </form>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="rounded-lg border bg-card px-8 py-10 text-center text-sm text-[var(--color-lop-text-secondary)] shadow-xl">
        {children}
      </div>
    </div>
  );
}
